import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import { enableReservations } from "../../api/workspaceSettings";
import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";

import { SettingsCard } from "./SettingsCard";
import { DisableReservationsDialog } from "./DisableReservationsDialog";

import { Switch } from "@/components/ui/switch";

export const ReservationsStatusCard = () => {
  const { t } = useTranslation(["workspaces"]);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { settings, setSettings } = useWorkspaceSettingsStore();
  const { refetch: refetchWorkspaceInfo } = useWorkspaceInformationStore();
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);

  if (!settings) return null;

  const { user_status } = settings;
  const isReservationsDisabled = user_status.has_reservations_disabled;

  const handleSwitchChange = async (checked: boolean) => {
    if (checked) {
      // Enabling reservations - call API directly
      if (!workspaceId) return;

      setIsEnabling(true);
      try {
        await enableReservations(workspaceId);

        // Update the workspace settings store locally
        setSettings({
          ...settings,
          user_status: {
            ...settings.user_status,
            has_reservations_disabled: false,
            reservations_disabled_reason: null,
            reservations_disabled_at: null,
            reservations_disabled_by: null,
          },
        });

        // Refetch workspace basics data
        await refetchWorkspaceInfo(workspaceId);
      } catch (error: any) {
        // Error is handled by the UI state and can be retried
      } finally {
        setIsEnabling(false);
      }
    } else {
      // Disabling reservations - open dialog
      setIsDisableDialogOpen(true);
    }
  };

  const getReservationsInfo = () => {
    if (isReservationsDisabled) {
      return {
        icon: "mdi:pause-circle",
        title: t("workspaces:settings.reservations.disabled", {
          defaultValue: "Reservations Disabled",
        }),
        description:
          user_status.reservations_disabled_reason ||
          t("workspaces:settings.reservations.disabledReason", {
            defaultValue: "New reservations are currently disabled",
          }),
        status: t("workspaces:settings.reservations.disabledStatus", { defaultValue: "Disabled" }),
        variant: "destructive" as const,
        topCircle: "from-red-600 to-pink-500",
        bottomCircle: "from-red-500 to-orange-500",
      };
    }

    return {
      icon: "mdi:calendar-check",
      title: t("workspaces:settings.reservations.enabled", {
        defaultValue: "Reservations Enabled",
      }),
      description: t("workspaces:settings.reservations.enabledDescription", {
        defaultValue: "Students can book lessons with you",
      }),
      status: t("workspaces:settings.reservations.enabledStatus", { defaultValue: "Enabled" }),
      variant: "default" as const,
      topCircle: "from-green-600 to-blue-500",
      bottomCircle: "from-green-500 to-teal-500",
    };
  };

  const reservationsInfo = getReservationsInfo();

  const action = (
    <div className="flex items-center gap-2 max-sm:flex-col">
      <Switch
        checked={!isReservationsDisabled}
        className="bg-accent-foreground! scale-75 data-[state=checked]:[&_>_span]:bg-emerald-500! data-[state=unchecked]:[&_>_span]:bg-red-600!"
        disabled={isEnabling}
        onCheckedChange={handleSwitchChange}
      />
    </div>
  );

  return (
    <>
      <SettingsCard
        action={action}
        bottomCircle={reservationsInfo.bottomCircle}
        bottomCirclePlacement="bottom-right"
        bottomCircleSize="h-24 w-24"
        description={reservationsInfo.description}
        icon={reservationsInfo.icon}
        title={reservationsInfo.title}
        topCircle={reservationsInfo.topCircle}
        topCirclePlacement="top-left"
        topCircleSize="h-20 w-20"
      />

      <DisableReservationsDialog
        isOpen={isDisableDialogOpen}
        onClose={() => setIsDisableDialogOpen(false)}
      />
    </>
  );
};
